import "./App.styles.scss";
import thunk from "redux-thunk";
import { createStore, applyMiddleware, compose } from "redux";
import { Provider, useDispatch } from "react-redux";
import { BrowserRouter as Router, useLocation, matchPath, useHistory } from "react-router-dom";
import RouterComponent from "./pages/router";

import rootReducer from "./reducers";
import { AppContext } from "./AppContext";
import useGlobalUserData from "@apiHooks/useGlobalUserData";
import {
    AppId,
    cssX,
    Env, getRem,
    GtmInit,
    IDropdownItem,
    PlatformContextProvider, PortalContextProvider,
    PortalSelector,
} from "@datassential/platform-ui-lib";
import { useContext, useMemo } from "react";
import { open as openPackagesPopup } from "@actions/packages.actions";
import cn from "classnames";
import { Slide, ToastContainer } from "react-toastify";
import WorkspaceSwitcher from "@pages/Home/components/WorkspaceSwitcher/WorkspaceSwitcher";
import { WorkspaceResolutionProvider } from "@pages/Home/context/WorkspaceResolutionContext";
import { routeNames } from "@core/routeNames";

// @ts-ignore-next-line
const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;
const createStoreWithInitialState = (initialState = {}) => {
    return createStore(rootReducer, initialState, composeEnhancers(applyMiddleware(thunk)));
};
const store = createStoreWithInitialState();

function AppContent() {
    const dispatch = useDispatch();
    const location = useLocation();
    const {
        globalVariables: {
            hasMacAdminAccess,
            availableWorkspaces,
        },
    } = useContext(AppContext);

    const env = useMemo(
        () => {
            switch ((process.env.REACT_APP_PLATFORM_ENV || '').toLowerCase()) {
                case 'qa':
                    return Env.QA;
                case 'staging':
                    return Env.Staging;
                default:
                    return Env.Live;
            }
        },
        []
    );

    const isHomeRoute = useMemo(
        () => matchPath(location.pathname, { path: routeNames.home, exact: true }) !== null,
        [location.pathname],
    );

    const userActions = useMemo(
        () => {
            const result: IDropdownItem[] = [];

            if (isHomeRoute && availableWorkspaces.length > 0) {
                result.push({
                    id: 'workspace-switcher',
                    content: <WorkspaceSwitcher/>,
                });
            }

            result.push({
                id: 'packages',
                content: (
                    <div
                        className={cn(cssX("Dropdown__item"), 'is-tall')}
                        data-testid="buttonPackagesPopupOpen"
                        onClick={() => dispatch(openPackagesPopup())}
                    >
                        Packages
                    </div>
                ),
            });

            if (hasMacAdminAccess) {
                result.push({
                    id: 'admin',
                    content: (
                        <a
                            href={`${process.env.REACT_APP_API}Admin/Mac`}
                            target={'_blank'}
                            rel="noreferrer"
                            data-testid="buttonMacsAdminLink"
                            className={cn(cssX("Dropdown__item"), 'is-tall')}
                        >
                            MAC Admin
                        </a>
                    ),
                });
            }

            return result;
        },
        [hasMacAdminAccess, dispatch, isHomeRoute, availableWorkspaces.length]
    );

    const history = useHistory();

    return (
        <PlatformContextProvider
            appId={AppId.Snap}
            env={env}
            enableDevTools={env !== Env.Live}
            additionalUserMenu={userActions}
        >
            <ToastContainer
                position="bottom-right"
                style={{ transform: `translateY(${getRem(-40)})` }}
                autoClose={3000}
                hideProgressBar
                closeOnClick
                theme="colored"
                transition={Slide}
            />
            {process.env.NODE_ENV !== 'development' && <GtmInit/>}
            <WorkspaceResolutionProvider>
                <RouterComponent/>
            </WorkspaceResolutionProvider>
        </PlatformContextProvider>
    );
}

function App() {
    const { data: globalVariables, isLoading } = useGlobalUserData();

    if (isLoading) return null;

    return (
        <Provider store={store}>
            <PortalContextProvider selector={PortalSelector.ModalRootPx}>
                <AppContext.Provider value={{ globalVariables }}>
                    <Router basename="/">
                        <AppContent/>
                    </Router>
                </AppContext.Provider>
            </PortalContextProvider>
        </Provider>
    );
}

export default App;
